/**
 * AuthTraceTab — Unified auth chain trace for debugging access issues.
 *
 * Shows the full auth resolution chain:
 *   JWT Claims → Role IDs → Resolved Scopes → Nav Filtering Result
 *
 * Surfaces PermissionDebugPanel logic in a visible tab (vs hidden DevTools).
 *
 * DRR-315: Unified Auth Trace
 */

import { useMemo, useState } from 'react';
import {
  Stack,
  Paper,
  Text,
  Group,
  Badge,
  Alert,
  Table,
  ThemeIcon,
  TextInput,
  SegmentedControl,
  Divider,
  ScrollArea,
  CopyButton,
  Button,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertTriangle,
  IconLock,
  IconFlag,
  IconSearch,
  IconCopy,
  IconAlertCircle,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';

import { useAuth, useViewLayer } from '@/auth/AuthContext';
import { useTenantSubgraphConfig, type SubgraphNavItem, type SubgraphRoute } from '@/hooks/useTenantSubgraphConfig';
import {
  useGetAllowedNavigationQuery,
  ViewLayer as GqlViewLayer,
  useMyRoleIdsQuery,
} from '@/generated/graphql';
import { useTenantFeatureFlags, type TenantFeatureFlags } from '@ferp/workflow-forms';
import {
  AuthChainVisualization,
  buildAuthChainSteps,
  type AuthTraceData,
} from './AuthChainVisualization';

// ── Types ─────────────────────────────────────────────────────────────────────

type ItemStatus = 'enabled' | 'missing_scope' | 'wrong_layer' | 'feature_disabled';

interface BlockedItem {
  path: string;
  label: string;
  status: ItemStatus;
  reason: string;
  requiredScope?: string;
  requiredLayer?: string;
  featureFlag?: string;
  subgraph?: string;
}

// ── Layer hierarchy (ARCHITECT > ADMIN > USER) ────────────────────────────────

const LAYER_RANK: Record<string, number> = {
  user: 1,
  admin: 2,
  architect: 3,
};

function canAccessLayer(userLayer: string, requiredLayer: string): boolean {
  const userRank = LAYER_RANK[userLayer.toLowerCase()] ?? 0;
  const requiredRank = LAYER_RANK[requiredLayer.toLowerCase()] ?? 0;
  return userRank >= requiredRank;
}

// ── Feature flag to path mapping ──────────────────────────────────────────────

const FEATURE_FLAG_PATHS: { flag: keyof TenantFeatureFlags; paths: string[] }[] = [
  { flag: 'careJourney', paths: ['/ferp-core/journey'] },
  { flag: 'beaconDashboard', paths: ['/alerts/beacon'] },
  { flag: 'witnessQueue', paths: ['/admin/witness-queue'] },
  { flag: 'standardsPack', paths: ['/ferp-core/compliance/standards-packs'] },
  { flag: 'syncDashboard', paths: ['/sync'] },
];

function getFeatureFlagForPath(path: string): keyof TenantFeatureFlags | null {
  for (const { flag, paths } of FEATURE_FLAG_PATHS) {
    if (paths.includes(path)) return flag;
  }
  return null;
}

// ── Subgraph detection from path ──────────────────────────────────────────────

function getSubgraphFromPath(path: string): string {
  if (path.startsWith('/admin/fhir')) return 'fhir';
  if (path.startsWith('/admin/payroll')) return 'payroll';
  if (path.startsWith('/admin/erp')) return 'erp';
  if (path.startsWith('/ferp-core')) return 'core';
  if (path.startsWith('/primitives')) return 'core';
  if (path.startsWith('/architect')) return 'core';
  if (path.startsWith('/admin')) return 'core';
  if (path.startsWith('/exchange')) return 'edi';
  if (path.startsWith('/sync')) return 'core';
  if (path.startsWith('/alerts')) return 'core';
  return 'core';
}

const SUBGRAPH_COLORS: Record<string, string> = {
  core: 'violet',
  fhir: 'blue',
  payroll: 'yellow',
  erp: 'green',
  edi: 'orange',
};

// ── Status colors and icons ───────────────────────────────────────────────────

const STATUS_CONFIG: Record<ItemStatus, { color: string; icon: React.ReactNode; label: string }> = {
  enabled: { color: 'green', icon: <IconCheck size={14} />, label: 'Enabled' },
  missing_scope: { color: 'orange', icon: <IconAlertTriangle size={14} />, label: 'Missing Scope' },
  wrong_layer: { color: 'red', icon: <IconLock size={14} />, label: 'Wrong Layer' },
  feature_disabled: { color: 'gray', icon: <IconFlag size={14} />, label: 'Feature Off' },
};

// ── Animation variants ────────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

// ── Component ─────────────────────────────────────────────────────────────────

export function AuthTraceTab() {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'blocked'>('blocked');

  const { tenant, idToken } = useAuth();
  const viewLayer = useViewLayer();
  const tenantId = tenant?.id ?? null;

  // Decode JWT claims
  const jwtClaims = useMemo(() => {
    if (!idToken) return null;
    try {
      const parts = idToken.split('.');
      if (parts.length !== 3) return null;
      const payloadPart = parts[1];
      if (!payloadPart) return null;
      const payload = JSON.parse(atob(payloadPart));
      return {
        email: payload.email,
        groups: payload.groups ?? payload.ferp_groups,
        ferp_roles: payload.ferp_roles,
        ferp_tenant: payload.ferp_tenant,
        exp: payload.exp,
      };
    } catch {
      return null;
    }
  }, [idToken]);

  // Get role IDs
  const { data: roleData, loading: roleLoading } = useMyRoleIdsQuery({
    skip: !tenantId,
  });
  const roleIds = roleData?.myRoleIds ?? [];

  // Get ALL nav items (unfiltered)
  const allConfig = useTenantSubgraphConfig(tenantId);
  const allNavItems = allConfig.config?.allNavItems ?? [];
  const allRoutes = allConfig.config?.allRoutes ?? [];

  // Get ALLOWED nav items (filtered by scopes)
  const gqlLayer = viewLayer.toUpperCase() as GqlViewLayer;
  const { data: allowedNavData, loading: navLoading } = useGetAllowedNavigationQuery({
    variables: { tenantId: tenantId ?? '', layer: gqlLayer },
    skip: !tenantId,
  });

  const userScopes = allowedNavData?.allowedNavigation?.userScopes ?? [];

  // Get feature flags
  const { flags: featureFlags } = useTenantFeatureFlags();

  // Build allowed sets for quick lookup
  const allowedPaths = useMemo(() => {
    const routes = allowedNavData?.allowedNavigation?.routes ?? [];
    const navItems = allowedNavData?.allowedNavigation?.navItems ?? [];
    const paths = new Set<string>();
    for (const r of routes) paths.add(r.path);
    for (const n of navItems) paths.add(n.path);
    return paths;
  }, [allowedNavData]);

  // Build blocked items list
  const { blockedItems, allowedCount } = useMemo(() => {
    const blocked: BlockedItem[] = [];
    let allowed = 0;
    const seen = new Set<string>();

    for (const item of allNavItems) {
      if (!item.path || seen.has(item.path)) continue;
      seen.add(item.path);

      const isAllowed = allowedPaths.has(item.path);

      if (isAllowed) {
        allowed++;
        continue;
      }

      const featureFlag = getFeatureFlagForPath(item.path);
      const featureDisabled = featureFlag && !featureFlags[featureFlag];

      let status: ItemStatus;
      let reason: string;
      let requiredScope: string | undefined;
      let requiredLayer: string | undefined;

      if (featureDisabled) {
        status = 'feature_disabled';
        reason = `Feature flag "${featureFlag}" is disabled`;
      } else if (item.layer && !canAccessLayer(viewLayer, item.layer)) {
        status = 'wrong_layer';
        reason = `Requires "${item.layer}" layer, you have "${viewLayer}"`;
        requiredLayer = item.layer;
      } else {
        status = 'missing_scope';
        const route = allRoutes.find((r: SubgraphRoute) => r.path === item.path);
        if (route?.requiredPermissions?.length) {
          requiredScope = route.requiredPermissions.join(', ');
          reason = `Missing scope: ${requiredScope}`;
        } else {
          reason = 'Missing required scope (check backend RequireScope guard)';
        }
      }

      blocked.push({
        path: item.path,
        label: item.label || item.path,
        status,
        reason,
        requiredScope,
        requiredLayer,
        featureFlag: featureFlag ?? undefined,
        subgraph: getSubgraphFromPath(item.path),
      });
    }

    // Sort: wrong_layer first, then missing_scope, then feature_disabled
    blocked.sort((a, b) => {
      const order: Record<ItemStatus, number> = {
        wrong_layer: 0,
        missing_scope: 1,
        feature_disabled: 2,
        enabled: 3,
      };
      const diff = order[a.status] - order[b.status];
      if (diff !== 0) return diff;
      return a.label.localeCompare(b.label);
    });

    return { blockedItems: blocked, allowedCount: allowed };
  }, [allNavItems, allRoutes, allowedPaths, viewLayer, featureFlags]);

  // Filter items
  const filteredItems = useMemo(() => {
    if (!filter) return blockedItems;
    const lowerFilter = filter.toLowerCase();
    return blockedItems.filter(
      i => i.path.toLowerCase().includes(lowerFilter) ||
           i.label.toLowerCase().includes(lowerFilter) ||
           i.requiredScope?.toLowerCase().includes(lowerFilter)
    );
  }, [blockedItems, filter]);

  // Build auth chain data for visualization
  const authTraceData: AuthTraceData = useMemo(() => ({
    jwtClaims,
    roleIds,
    resolvedScopes: userScopes,
    blockedItems: blockedItems.map(item => ({
      path: item.path,
      label: item.label,
      reason: item.reason,
      requiredScope: item.requiredScope,
    })),
    allowedCount,
  }), [jwtClaims, roleIds, userScopes, blockedItems, allowedCount]);

  const chainSteps = useMemo(() => buildAuthChainSteps(authTraceData), [authTraceData]);

  // Build copy-able JSON
  const debugJson = useMemo(() => JSON.stringify({
    timestamp: new Date().toISOString(),
    viewLayer,
    tenant: tenant?.name ?? null,
    jwtClaims,
    roleIds,
    resolvedScopes: userScopes,
    allowedCount,
    blockedCount: blockedItems.length,
    blockedItems: blockedItems.map(i => ({
      path: i.path,
      label: i.label,
      status: i.status,
      reason: i.reason,
      requiredScope: i.requiredScope ?? null,
      requiredLayer: i.requiredLayer ?? null,
    })),
  }, null, 2), [viewLayer, tenant, jwtClaims, roleIds, userScopes, allowedCount, blockedItems]);

  const loading = roleLoading || navLoading;

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="md" />
          <Text size="sm" c="dimmed">Loading auth chain...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="show"
    >
      <Stack gap="md">
        {/* Chain Visualization */}
        <motion.div variants={fadeUp}>
          <Paper p="md" withBorder radius="md">
            <AuthChainVisualization steps={chainSteps} />
          </Paper>
        </motion.div>

        {/* Context Summary */}
        <motion.div variants={fadeUp}>
          <Alert color="blue" variant="light">
            <Group gap="md" wrap="wrap">
              <Group gap="xs">
                <Text size="xs" fw={600}>View Layer:</Text>
                <Badge size="sm" color={
                  viewLayer === 'architect' ? 'violet' :
                  viewLayer === 'admin' ? 'orange' : 'blue'
                }>
                  {viewLayer}
                </Badge>
              </Group>
              <Group gap="xs">
                <Text size="xs" fw={600}>Tenant:</Text>
                <Badge size="sm" variant="outline">{tenant?.name ?? 'none'}</Badge>
              </Group>
              <Group gap="xs">
                <Text size="xs" fw={600}>Roles:</Text>
                {roleIds.length > 0 ? (
                  roleIds.slice(0, 3).map(id => (
                    <Badge key={id} size="sm" variant="outline" color="gray">{id}</Badge>
                  ))
                ) : (
                  <Text size="xs" c="dimmed">No roles</Text>
                )}
                {roleIds.length > 3 && (
                  <Badge size="sm" color="gray">+{roleIds.length - 3} more</Badge>
                )}
              </Group>
            </Group>
          </Alert>
        </motion.div>

        {/* Blocked Items Section */}
        <motion.div variants={fadeUp}>
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Group justify="space-between">
                <div>
                  <Text fw={600}>Navigation Filtering Results</Text>
                  <Text size="xs" c="dimmed">
                    Items blocked based on scopes, layer, or feature flags
                  </Text>
                </div>
                <Group gap="xs">
                  <Badge size="sm" color="green" variant="light">{allowedCount} allowed</Badge>
                  <Badge size="sm" color="orange" variant="light">{blockedItems.length} blocked</Badge>
                  <CopyButton value={debugJson}>
                    {({ copied, copy }) => (
                      <Button
                        size="xs"
                        variant={copied ? 'filled' : 'light'}
                        color={copied ? 'teal' : 'gray'}
                        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        onClick={copy}
                      >
                        {copied ? 'Copied!' : 'Export'}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              </Group>

              <Divider />

              {/* Filters */}
              <Group gap="xs">
                <TextInput
                  size="xs"
                  placeholder="Search path, label, or scope..."
                  leftSection={<IconSearch size={14} />}
                  value={filter}
                  onChange={(e) => setFilter(e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <SegmentedControl
                  size="xs"
                  value={statusFilter}
                  onChange={(v) => setStatusFilter(v as 'all' | 'blocked')}
                  data={[
                    { label: 'Blocked Only', value: 'blocked' },
                    { label: 'All', value: 'all' },
                  ]}
                />
              </Group>

              {/* Blocked Items Table */}
              {blockedItems.length === 0 ? (
                <Alert icon={<IconCheck size={16} />} color="green" title="Full Access">
                  All navigation items are accessible for your current scopes and view layer.
                </Alert>
              ) : filteredItems.length === 0 ? (
                <Alert icon={<IconAlertCircle size={16} />} color="gray">
                  No blocked items match your filter.
                </Alert>
              ) : (
                <ScrollArea h={350} offsetScrollbars>
                  <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 30 }}></Table.Th>
                        <Table.Th>Path / Label</Table.Th>
                        <Table.Th>Why Blocked</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredItems.map((item) => {
                        const config = STATUS_CONFIG[item.status];
                        return (
                          <Table.Tr key={item.path}>
                            <Table.Td>
                              <ThemeIcon size="sm" color={config.color} variant="light">
                                {config.icon}
                              </ThemeIcon>
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={2}>
                                <Group gap={4}>
                                  <Text size="xs" fw={500}>{item.label}</Text>
                                  {item.subgraph && (
                                    <Badge size="xs" variant="dot" color={SUBGRAPH_COLORS[item.subgraph] ?? 'gray'}>
                                      {item.subgraph}
                                    </Badge>
                                  )}
                                </Group>
                                <Text size="xs" c="dimmed" ff="monospace">{item.path}</Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={2}>
                                <Badge size="xs" color={config.color} variant="filled">
                                  {config.label}
                                </Badge>
                                {item.requiredScope && (
                                  <Text size="xs" c="dimmed" ff="monospace">
                                    Need: {item.requiredScope}
                                  </Text>
                                )}
                                {item.requiredLayer && (
                                  <Text size="xs" c="dimmed">
                                    Need layer: {item.requiredLayer}
                                  </Text>
                                )}
                                {item.featureFlag && (
                                  <Text size="xs" c="dimmed">
                                    Flag: {item.featureFlag}
                                  </Text>
                                )}
                              </Stack>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Stack>
          </Paper>
        </motion.div>

        {/* Resolved Scopes Preview */}
        <motion.div variants={fadeUp}>
          <Paper p="md" withBorder radius="md">
            <Group justify="space-between" mb="sm">
              <div>
                <Text fw={600}>Resolved Scopes</Text>
                <Text size="xs" c="dimmed">
                  Scopes resolved from your roles (first 30 shown)
                </Text>
              </div>
              <Badge size="lg" variant="light">{userScopes.length} scopes</Badge>
            </Group>
            {userScopes.length === 0 ? (
              <Alert icon={<IconAlertTriangle size={16} />} color="orange">
                No scopes resolved. Check JWT claims and role mappings.
              </Alert>
            ) : (
              <Group gap={4}>
                {userScopes.slice(0, 30).map(scope => (
                  <Badge key={scope} size="xs" variant="light" ff="monospace">{scope}</Badge>
                ))}
                {userScopes.length > 30 && (
                  <Badge size="xs" color="gray">+{userScopes.length - 30} more</Badge>
                )}
              </Group>
            )}
          </Paper>
        </motion.div>

        {/* Help Text */}
        <motion.div variants={fadeUp}>
          <Text size="xs" c="dimmed">
            This tab traces the full auth chain: JWT → Roles → Scopes → Navigation.
            Use the Export button to copy full debug data for support.
            See also: <kbd>Ctrl+Shift+D</kbd> for floating debug panel.
          </Text>
        </motion.div>
      </Stack>
    </motion.div>
  );
}
